<!--
  ResetPasswordView.vue
  Password reset page for users who clicked reset link in email

  Props:
  - token: the password reset token from URL

  Emits:
  - success: when password is reset successfully
  - back-to-login: when user wants to go back to login
-->

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useProfile } from '@/composables/useProfile'

const props = defineProps<{
  token: string
}>()

const emit = defineEmits<{
  success: []
  'back-to-login': []
}>()

const { validateResetToken, confirmPasswordReset, loading } = useProfile()

// State
const validating = ref(true)
const tokenValid = ref(false)
const tokenMessage = ref('')
const newPassword = ref('')
const confirmPassword = ref('')
const showPassword = ref(false)
const errorMessage = ref('')
const resetComplete = ref(false)

// Validation
const passwordStrength = computed(() => {
  const pwd = newPassword.value
  if (!pwd) return { score: 0, label: '', color: '' }

  let score = 0
  if (pwd.length >= 8) score++
  if (/[A-Z]/.test(pwd)) score++
  if (/[a-z]/.test(pwd)) score++
  if (/\d/.test(pwd)) score++
  if (/[^A-Za-z0-9]/.test(pwd)) score++

  const labels = ['Weak', 'Fair', 'Good', 'Strong', 'Very Strong']
  const colors = ['#ef4444', '#f59e0b', '#eab308', '#22c55e', '#10b981']

  return {
    score,
    label: labels[Math.min(score - 1, 4)] || '',
    color: colors[Math.min(score - 1, 4)] || '#ef4444',
  }
})

const passwordsMatch = computed(() => {
  if (!confirmPassword.value) return true
  return newPassword.value === confirmPassword.value
})

const isFormValid = computed(() => {
  return (
    newPassword.value.length >= 8 &&
    passwordStrength.value.score >= 3 &&
    passwordsMatch.value
  )
})

// Validate token on mount
onMounted(async () => {
  const result = await validateResetToken(props.token)
  tokenValid.value = result.valid
  tokenMessage.value = result.message
  validating.value = false
})

async function handleSubmit() {
  if (!isFormValid.value) return

  errorMessage.value = ''

  try {
    await confirmPasswordReset(props.token, newPassword.value, confirmPassword.value)
    resetComplete.value = true
  } catch (err: unknown) {
    errorMessage.value = err instanceof Error ? err.message : 'Failed to reset password'
  }
}
</script>

<template>
  <div class="reset-password-view">
    <div class="reset-card">
      <!-- Logo/Header -->
      <div class="card-header">
        <h1>Reset Password</h1>
      </div>

      <div class="card-content">
        <!-- Loading State -->
        <div v-if="validating" class="loading-state">
          <div class="spinner"></div>
          <span>Verifying reset link...</span>
        </div>

        <!-- Invalid Token State -->
        <div v-else-if="!tokenValid" class="error-state">
          <div class="error-icon">
            <span class="material-symbols-outlined">link_off</span>
          </div>
          <h2>Invalid or Expired Link</h2>
          <p>{{ tokenMessage }}</p>
          <button class="btn-primary" @click="emit('back-to-login')">
            Back to Login
          </button>
        </div>

        <!-- Success State -->
        <div v-else-if="resetComplete" class="success-state">
          <div class="success-icon">
            <span class="material-symbols-outlined">check_circle</span>
          </div>
          <h2>Password Reset!</h2>
          <p>Your password has been successfully reset. You can now log in with your new password.</p>
          <button class="btn-primary" @click="emit('success')">
            Go to Login
          </button>
        </div>

        <!-- Reset Form -->
        <template v-else>
          <p class="description">
            Create a new password for your account. Make sure it's at least 8 characters with
            uppercase, lowercase, and numbers.
          </p>

          <form @submit.prevent="handleSubmit">
            <div class="form-group">
              <label for="new-password">New Password</label>
              <div class="password-input-wrapper">
                <input
                  id="new-password"
                  v-model="newPassword"
                  :type="showPassword ? 'text' : 'password'"
                  required
                  :disabled="loading"
                  autocomplete="new-password"
                  minlength="8"
                  placeholder="Enter new password"
                />
                <button
                  type="button"
                  class="password-toggle"
                  @click="showPassword = !showPassword"
                  tabindex="-1"
                >
                  <span class="material-symbols-outlined">
                    {{ showPassword ? 'visibility_off' : 'visibility' }}
                  </span>
                </button>
              </div>
              <div v-if="newPassword" class="password-strength">
                <div class="strength-bar">
                  <div
                    class="strength-fill"
                    :style="{
                      width: `${(passwordStrength.score / 5) * 100}%`,
                      backgroundColor: passwordStrength.color,
                    }"
                  ></div>
                </div>
                <span class="strength-label" :style="{ color: passwordStrength.color }">
                  {{ passwordStrength.label }}
                </span>
              </div>
            </div>

            <div class="form-group">
              <label for="confirm-password">Confirm Password</label>
              <input
                id="confirm-password"
                v-model="confirmPassword"
                type="password"
                required
                :disabled="loading"
                autocomplete="new-password"
                placeholder="Confirm new password"
              />
              <span v-if="!passwordsMatch" class="error-text">Passwords do not match</span>
            </div>

            <div v-if="errorMessage" class="error-message">
              {{ errorMessage }}
            </div>

            <button type="submit" class="btn-primary submit-btn" :disabled="!isFormValid || loading">
              {{ loading ? 'Resetting...' : 'Reset Password' }}
            </button>
          </form>

          <div class="back-link">
            <button type="button" class="link-button" @click="emit('back-to-login')">
              Back to Login
            </button>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<style scoped>
.reset-password-view {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem 1rem;
  background: linear-gradient(135deg, #0d1f2d 0%, #1a3a4a 50%, #0d1f2d 100%);
}

.reset-card {
  background: linear-gradient(135deg, rgba(13, 31, 45, 0.95) 0%, rgba(26, 47, 63, 0.95) 100%);
  border-radius: 16px;
  box-shadow:
    0 24px 64px rgba(0, 0, 0, 0.5),
    0 0 0 1px rgba(255, 255, 255, 0.1);
  width: 100%;
  max-width: 420px;
  overflow: hidden;
}

.card-header {
  text-align: center;
  padding: 2rem 1.5rem 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.card-header h1 {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 700;
  color: #fff;
}

.card-content {
  padding: 1.5rem;
}

/* States */
.loading-state,
.error-state,
.success-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 1rem 0;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(255, 255, 255, 0.1);
  border-top-color: #d4a72c;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.loading-state span {
  color: rgba(255, 255, 255, 0.7);
}

.error-icon,
.success-icon {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.25rem;
}

.error-icon {
  background: rgba(239, 68, 68, 0.15);
}

.error-icon .material-symbols-outlined {
  font-size: 2.5rem;
  color: #ef4444;
}

.success-icon {
  background: rgba(16, 185, 129, 0.15);
}

.success-icon .material-symbols-outlined {
  font-size: 2.5rem;
  color: #10b981;
}

.error-state h2,
.success-state h2 {
  margin: 0 0 0.75rem;
  font-size: 1.25rem;
  color: #fff;
}

.error-state p,
.success-state p {
  margin: 0 0 1.5rem;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.5;
}

/* Form */
.description {
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.9375rem;
  margin: 0 0 1.5rem;
  line-height: 1.5;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  margin-bottom: 1rem;
}

.form-group label {
  font-size: 0.8125rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.7);
}

.form-group input {
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.12);
  color: #fff;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  font-size: 0.9375rem;
  transition: all 0.2s;
}

.form-group input:focus {
  outline: none;
  border-color: rgba(212, 167, 44, 0.5);
  box-shadow: 0 0 0 3px rgba(212, 167, 44, 0.1);
}

.form-group input::placeholder {
  color: rgba(255, 255, 255, 0.3);
}

.password-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.password-input-wrapper input {
  flex: 1;
  padding-right: 44px;
}

.password-toggle {
  position: absolute;
  right: 8px;
  background: transparent;
  border: none;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.4);
  padding: 4px;
  display: flex;
  transition: color 0.2s;
}

.password-toggle:hover {
  color: rgba(255, 255, 255, 0.7);
}

.password-toggle .material-symbols-outlined {
  font-size: 1.25rem;
}

.password-strength {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.25rem;
}

.strength-bar {
  flex: 1;
  height: 4px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 2px;
  overflow: hidden;
}

.strength-fill {
  height: 100%;
  transition: all 0.3s;
  border-radius: 2px;
}

.strength-label {
  font-size: 0.75rem;
  font-weight: 500;
  min-width: 70px;
}

.error-text {
  font-size: 0.75rem;
  color: #ef4444;
}

.error-message {
  background: rgba(239, 68, 68, 0.15);
  border: 1px solid rgba(239, 68, 68, 0.3);
  color: #fca5a5;
  padding: 0.625rem 0.875rem;
  border-radius: 8px;
  font-size: 0.8125rem;
  margin-bottom: 1rem;
}

.submit-btn {
  width: 100%;
  margin-bottom: 1rem;
}

.btn-primary {
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-size: 0.9375rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  background: linear-gradient(135deg, #d4a72c 0%, #b58e1d 100%);
  border: none;
  color: #1a2f3f;
}

.btn-primary:hover:not(:disabled) {
  background: linear-gradient(135deg, #e5b933 0%, #c99f2e 100%);
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.back-link {
  text-align: center;
  padding-top: 0.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.link-button {
  background: transparent;
  border: none;
  color: #d4a72c;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  padding: 0.5rem;
  transition: color 0.2s;
}

.link-button:hover {
  color: #e5b933;
}
</style>
